/**
 * Durable attempt history. Uses Firestore (per-user, multi-device) when Firebase
 * is configured; otherwise falls back to the backend SQLite API.
 */
import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  orderBy,
  query,
} from "firebase/firestore";
import { isFirebaseConfigured, getDb } from "./firebase";
import { api, type ExamResultsResponse } from "./api";
import type { Attempt } from "@/stores/attempts";

function resultToAttempt(r: ExamResultsResponse): Omit<Attempt, "id" | "date"> {
  return {
    examId: r.exam_id,
    title: r.title,
    totalCorrect: r.total_correct,
    totalQuestions: r.total_questions,
    overallPercent: r.overall_percent,
    estimatedLow: r.estimated_score_range[0],
    estimatedHigh: r.estimated_score_range[1],
    sectionScores: r.section_scores,
    weakTopics: r.weak_topics,
    strengths: r.strengths ?? [],
    totalSeconds: r.timing?.total_seconds ?? 0,
  };
}

export async function saveAttempt(studentId: string, r: ExamResultsResponse): Promise<void> {
  const base = resultToAttempt(r);
  if (isFirebaseConfigured) {
    await addDoc(collection(getDb(), "users", studentId, "attempts"), {
      ...base,
      date: Date.now(),
    });
    return;
  }
  await api.recordAttempt({
    student_id: studentId,
    exam_id: r.exam_id,
    title: r.title,
    total_correct: r.total_correct,
    total_questions: r.total_questions,
    overall_percent: r.overall_percent,
    est_low: r.estimated_score_range[0],
    est_high: r.estimated_score_range[1],
    total_seconds: r.timing?.total_seconds ?? 0,
    section_scores: r.section_scores.map((s) => ({
      section_id: s.section_id,
      label: s.label,
      correct: s.correct,
      total: s.total,
      percent: s.percent,
    })),
    weak_topics: r.weak_topics,
    strengths: r.strengths ?? [],
  });
}

export async function fetchAttempts(studentId: string): Promise<Attempt[]> {
  if (isFirebaseConfigured) {
    const snap = await getDocs(
      query(collection(getDb(), "users", studentId, "attempts"), orderBy("date", "desc")),
    );
    return snap.docs.map((d) => {
      const v = d.data() as Omit<Attempt, "id">;
      return { id: d.id, ...v };
    });
  }
  const res = await api.listAttempts(studentId);
  return res.attempts.map((a) => ({
    id: a.id,
    date: a.created_at,
    examId: a.exam_id ?? "",
    title: a.title,
    totalCorrect: a.total_correct,
    totalQuestions: a.total_questions,
    overallPercent: a.overall_percent,
    estimatedLow: a.est_low,
    estimatedHigh: a.est_high,
    sectionScores: a.section_scores,
    weakTopics: a.weak_topics,
    strengths: a.strengths,
    totalSeconds: a.total_seconds,
  }));
}

export async function clearAttempts(studentId: string): Promise<void> {
  if (isFirebaseConfigured) {
    const snap = await getDocs(collection(getDb(), "users", studentId, "attempts"));
    await Promise.all(snap.docs.map((d) => deleteDoc(d.ref)));
    return;
  }
  await api.clearAttempts(studentId);
}
